import { Badge, BlockStack, Card, InlineStack, Text } from '@shopify/polaris'
import type { BccData } from './useBccData'
import type { GovernorateMetric } from '../../sdk/fc.api'
import { CardHeader } from './LensBadge'

function deliveryTone(rate: number): 'success' | 'caution' | 'critical' {
  if (rate >= 83) return 'success'
  if (rate >= 72) return 'caution'
  return 'critical'
}

function returnTone(rate: number): 'success' | 'caution' | 'critical' {
  if (rate <= 15) return 'success'
  if (rate <= 25) return 'caution'
  return 'critical'
}

// color bar width relative to max orders in list
function barWidth(count: number, max: number): string {
  return `${Math.max(6, Math.round((count / max) * 100))}%`
}

type Props = { data: BccData }

export function RegionalIntelligence({ data }: Props) {
  const { regional } = data

  // Sort by order count desc, take top 10
  const sorted = [...regional.governorates]
    .filter(g => g.governorate !== 'Unknown' && g.orderCount > 0)
    .sort((a, b) => b.orderCount - a.orderCount)
    .slice(0, 10)

  const maxOrders = sorted[0]?.orderCount ?? 1

  const mappedPct = regional.totalOrders > 0
    ? Math.round((regional.mappedOrders / regional.totalOrders) * 100)
    : 0

  return (
    <Card>
      <BlockStack gap="400">

        <CardHeader title="Regional Intelligence" lens="performance" maturity={data.dashboard.maturity} />

        <InlineStack align="end" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            {regional.unknownOrders > 0 && (
              <Text as="p" variant="bodySm" tone="subdued">
                {regional.unknownOrders} unmapped
              </Text>
            )}
            <Badge tone={mappedPct >= 90 ? 'success' : mappedPct >= 75 ? 'warning' : 'critical'}>
              {`${mappedPct}% mapped`}
            </Badge>
          </InlineStack>
        </InlineStack>

        {/* Header row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '140px 1fr 72px 72px 72px',
          gap: '0 8px',
          padding: '0 4px',
        }}>
          <Text as="p" variant="bodySm" tone="subdued">Governorate</Text>
          <Text as="p" variant="bodySm" tone="subdued">Orders</Text>
          <Text as="p" variant="bodySm" tone="subdued" alignment="end">Revenue</Text>
          <Text as="p" variant="bodySm" tone="subdued" alignment="end">Delivery</Text>
          <Text as="p" variant="bodySm" tone="subdued" alignment="end">Return</Text>
        </div>

        <BlockStack gap="150">
          {sorted.map((g: GovernorateMetric) => {
            // Same denominator the engine uses for deliveryRate (resolved orders),
            // so the two columns sum to 100% per governorate.
            const resolved   = g.deliveredCount + g.returnCount
            const returnRate = resolved > 0
              ? (g.returnCount / resolved) * 100
              : 0
            const dTone = deliveryTone(g.deliveryRate)
            const rTone = returnTone(returnRate)
            const revenueK = g.revenue >= 1000
              ? `${Math.round(g.revenue / 1000)}K`
              : `${Math.round(g.revenue)}`

            return (
              <div
                key={g.governorate}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '140px 1fr 72px 72px 72px',
                  gap: '0 8px',
                  alignItems: 'center',
                  padding: '6px 4px',
                  background: 'var(--p-color-bg-surface-secondary)',
                  borderRadius: 6,
                }}
              >
                {/* Name */}
                <Text as="p" variant="bodySm" fontWeight="semibold">{g.governorate}</Text>

                {/* Bar + count */}
                <InlineStack gap="200" blockAlign="center">
                  <div style={{
                    flex: 1,
                    height: 6,
                    background: 'var(--p-color-border)',
                    borderRadius: 3,
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: barWidth(g.orderCount, maxOrders),
                      height: '100%',
                      background: 'var(--p-color-bg-fill-brand)',
                      borderRadius: 3,
                    }} />
                  </div>
                  <Text as="p" variant="bodySm" tone="subdued">{g.orderCount}</Text>
                </InlineStack>

                {/* Revenue */}
                <Text as="p" variant="bodySm" alignment="end">{revenueK}</Text>

                {/* Delivery rate */}
                <Text
                  as="p"
                  variant="bodySm"
                  alignment="end"
                  tone={dTone === 'success' ? 'success' : dTone === 'critical' ? 'critical' : undefined}
                  fontWeight={dTone === 'critical' ? 'semibold' : undefined}
                >
                  {g.deliveryRate.toFixed(0)}%
                </Text>

                {/* Return rate */}
                <Text
                  as="p"
                  variant="bodySm"
                  alignment="end"
                  tone={rTone === 'critical' ? 'critical' : rTone === 'success' ? 'success' : undefined}
                  fontWeight={rTone === 'critical' ? 'semibold' : undefined}
                >
                  {returnRate.toFixed(0)}%
                </Text>
              </div>
            )
          })}
        </BlockStack>

        {sorted.length === 0 && (
          <Text as="p" variant="bodySm" tone="subdued">No regional data available for this month</Text>
        )}

      </BlockStack>
    </Card>
  )
}
